/**
 * Travel Planner Service
 * API client for the travel planner module.
 */

import { apiGet, apiPost } from '@/services/api/baseService';

// ── Types ──────────────────────────────────────────────────────────

export type TravelMode = 'car' | 'bicycle' | 'foot' | 'transit';

export interface GeoPoint {
  lat: number;
  lon: number;
}

export interface GeocodeSuggestion {
  placeId: number;
  displayName: string;
  lat: number;
  lon: number;
  type: string;
  addressType: string;
  address?: {
    road?: string;
    houseNumber?: string;
    postcode?: string;
    city?: string;
    county?: string;
    country?: string;
  };
}

export interface RouteResult {
  distance: number; // meters
  duration: number; // seconds
  geometry: GeoPoint[];
}

export interface MultiRouteResult {
  routes: RouteResult[];
}

export interface TransitLeg {
  name: string;
  type: string;
  direction: string;
  origin: { name: string; lat: number; lon: number; time: string; date: string };
  destination: { name: string; lat: number; lon: number; time: string; date: string };
  notes?: string[];
}

export interface TransitTrip {
  legs: TransitLeg[];
  duration: number;
  transfers: number;
  departureTime: string;
  arrivalTime: string;
}

export interface TransitSearchResult {
  trips: TransitTrip[];
  error?: 'NOT_CONFIGURED' | 'API_ERROR';
  message?: string;
}

export interface StopResult {
  id: string;
  name: string;
  lat: number;
  lon: number;
}

// ── API calls ──────────────────────────────────────────────────────

export async function geocodeSearch(query: string, limit = 5): Promise<GeocodeSuggestion[]> {
  return apiGet<GeocodeSuggestion[]>('/api/travel/geocode', {
    params: { q: query, limit },
  });
}

export async function calculateRoute(
  from: GeoPoint,
  to: GeoPoint,
  mode: TravelMode = 'car',
  options?: { alternatives?: boolean; avoid?: string[]; waypoints?: GeoPoint[] }
): Promise<MultiRouteResult> {
  const params: Record<string, string> = {
    fromLat: String(from.lat),
    fromLon: String(from.lon),
    toLat: String(to.lat),
    toLon: String(to.lon),
    mode,
  };

  if (options?.alternatives) params.alternatives = 'true';
  if (options?.avoid?.length) params.avoid = options.avoid.join(',');
  if (options?.waypoints?.length) {
    params.wp = options.waypoints.map(p => `${p.lat},${p.lon}`).join('|');
  }

  return apiGet<MultiRouteResult>('/api/travel/route', { params });
}

export async function searchTransit(
  from: GeoPoint,
  to: GeoPoint,
  options?: { date?: string; time?: string; arriveBy?: boolean }
): Promise<TransitSearchResult> {
  return apiGet<TransitSearchResult>('/api/travel/transit', {
    params: {
      fromLat: from.lat,
      fromLon: from.lon,
      toLat: to.lat,
      toLon: to.lon,
      ...options,
    },
  });
}

export async function searchStops(query: string): Promise<StopResult[]> {
  return apiGet<StopResult[]>('/api/travel/stops', { params: { q: query } });
}

export async function sendDirectionsEmail(data: {
  to: string;
  fromAddress: string;
  toAddress: string;
  mode: string;
  osmUrl?: string;
}): Promise<void> {
  await apiPost('/api/travel/email', data);
}
